import { TreatmentGroup, TreatmentGroupSession } from '@/types/treatmentGroup';
import { daysUntil, dateStringInZone, getBrowserTimeZone, getThisWeeksMonday } from '@/utils/dates';

const SESSION_LETTERS = 'abcdefghijklmnopqrstuvwxyz';

// Mirrors the backend's TreatmentGroup.display_as_of: the later of the
// group's start date and this week's Monday. A draft has no start date
// yet, and a published group's start date can still be in the future --
// in either case, this is what "current" means for that group's schedule,
// so its intended sessions/assignments read as current ahead of go-live
// instead of not-yet-started.
export const displayAsOf = (group: Pick<TreatmentGroup, 'start_date'>): string => {
  const thisMonday = getThisWeeksMonday();
  return group.start_date && group.start_date > thisMonday ? group.start_date : thisMonday;
};

// Whether the group's own effective date has actually arrived -- distinct
// from displayAsOf, which deliberately looks past a future start date.
// Used to tell "the group's own not-yet-started schedule" (still the
// current one to show) apart from "a session added mid-lifecycle to an
// already-running group" (a genuine upcoming change).
export const groupHasStarted = (group: Pick<TreatmentGroup, 'start_date'>): boolean =>
  !!group.start_date && daysUntil(`${group.start_date}T00:00:00`) <= 0;

// A group's end_date is a calendar date in the group's own timezone, not
// the viewer's -- comparing it against the browser's local "today" (as
// daysUntil does) can misjudge whether the group has already ended for
// anyone outside that timezone, especially right around midnight.
// dateStringInZone resolves what "today" actually is in the group's own
// zone first, so the comparison is a plain string compare from there. Falls
// back to the browser's own zone for a missing/invalid group timezone.
export const hasGroupEnded = (group: Pick<TreatmentGroup, 'end_date' | 'timezone'>): boolean => {
  if (!group.end_date) return false;
  const now = new Date().toISOString();
  let today: string;
  try {
    today = dateStringInZone(now, group.timezone);
  } catch {
    today = dateStringInZone(now, getBrowserTimeZone());
  }
  return group.end_date <= today;
};

// Mirrors the backend's GroupSessionMembershipQuerySet.open_as_of /
// TreatmentGroup.in_effect_sessions: a session already started (or
// starting) by asOf, and not yet ended before asOf. Used to restrict which
// sessions a client can pick for a member: the server-side letter map is
// only built from this same set, so a session outside it has no valid
// letter to submit.
export const sessionsInEffectAsOf = (sessions: TreatmentGroupSession[], asOf: string) => {
  return sessions.filter(
    (session) =>
      session.effective_from <= asOf && (!session.effective_until || session.effective_until > asOf)
  );
};

// Mirrors the backend's TreatmentGroup._letter_map: sessions sorted by
// (day_of_week, start_time), labeled a, b, c... -- lets a client submit a
// member's session selection as letters instead of ids, matching what the
// server stores (or will store, once a brand-new group's own sessions are
// persisted in this same order).
export const sessionLetterMap = (
  sessions: { dayOfWeek: number; id: string; startTime: string }[]
): Map<string, string> =>
  new Map(
    [...sessions]
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.startTime.localeCompare(b.startTime))
      .map((session, index) => [session.id, SESSION_LETTERS[index]])
  );

export const lettersForSessionIds = (
  sessionIds: string[],
  letterMap: Map<string, string>
): string[] => {
  const letters = sessionIds
    .map((id) => letterMap.get(id))
    .filter((letter): letter is string => !!letter);
  return [...new Set(letters)].sort();
};

export const uniqueCounselors = (sessions: TreatmentGroupSession[]) => {
  const byStaffId = new Map<string, TreatmentGroupSession>();
  sessions.forEach((session) => byStaffId.set(session.staff_id, session));
  return [...byStaffId.values()];
};

export const formatCounselors = (sessions: TreatmentGroupSession[]) => {
  const counselors = uniqueCounselors(sessions);
  if (counselors.length === 0) return '';
  if (counselors.length === 1) return counselors[0].staff_name;
  return counselors
    .map((c) => c.staff_last_name ?? c.staff_name)
    .sort((a, b) => a.localeCompare(b))
    .join(', ');
};

const staffFirstName = (session: TreatmentGroupSession) => {
  if (session.staff_last_name && session.staff_name.endsWith(session.staff_last_name)) {
    return session.staff_name.slice(0, -session.staff_last_name.length).trim();
  }
  return session.staff_name.split(' ')[0];
};

// Card-specific summary ("3 counselors - Maria, John, Sam") -- distinct from
// formatCounselors, which the list page's Counselor column also uses and
// keeps in its existing last-name format.
export const formatCounselorsForCard = (sessions: TreatmentGroupSession[]) => {
  const counselors = uniqueCounselors(sessions);
  if (counselors.length === 0) return '';
  if (counselors.length === 1) return counselors[0].staff_name;
  const firstNames = counselors.map(staffFirstName).sort((a, b) => a.localeCompare(b));
  return `${counselors.length} counselors - ${firstNames.join(', ')}`;
};
